import { useCallback, useState } from "react";

export enum ImagePreviewStatus {
  Loading = "loading",
  Broken = "broken",
  Ready = "ready",
}

interface ResolveImagePreviewStatusParams {
  src: string;
  loading: boolean;
  failedSrc: string | null;
}

interface UseImagePreviewControllerParams {
  src: string;
  loading: boolean;
  onClick?: () => void;
}

export interface ImagePreviewController {
  status: ImagePreviewStatus;
  clickable: boolean;
  isModalOpen: boolean;
  usesBuiltInLightbox: boolean;
  closeModal: () => void;
  handleImageError: () => void;
  handleRootClick: () => void;
}

function resolveImagePreviewStatus({
  src,
  loading,
  failedSrc,
}: ResolveImagePreviewStatusParams): ImagePreviewStatus {
  if (loading) {
    return ImagePreviewStatus.Loading;
  }

  if (failedSrc === src) {
    return ImagePreviewStatus.Broken;
  }

  if (!src.trim()) {
    return ImagePreviewStatus.Broken;
  }

  return ImagePreviewStatus.Ready;
}

export function useImagePreviewController({
  src,
  loading,
  onClick,
}: UseImagePreviewControllerParams): ImagePreviewController {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [failedSrc, setFailedSrc] = useState<string | null>(null);
  const status = resolveImagePreviewStatus({ src, loading, failedSrc });
  const clickable = status === ImagePreviewStatus.Ready;
  const usesBuiltInLightbox = !onClick;

  const closeModal = useCallback((): void => setIsModalOpen(false), []);
  const handleImageError = useCallback((): void => setFailedSrc(src), [src]);
  const handleRootClick = useCallback((): void => {
    if (onClick) {
      onClick();
      return;
    }

    setIsModalOpen(true);
  }, [onClick]);

  return {
    status,
    clickable,
    isModalOpen,
    usesBuiltInLightbox,
    closeModal,
    handleImageError,
    handleRootClick,
  };
}
